import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchBatchAssessments, fetchBatchDetail } from "./adminCourseService";
import "./adminCourses.css";

const CRITERIA = [
  { key: "scoreFluency",     label: "Fluency"     },
  { key: "scoreClarity",     label: "Clarity"     },
  { key: "scoreVocabulary",  label: "Vocabulary"  },
  { key: "scoreGrammar",     label: "Grammar"     },
  { key: "scoreInteraction", label: "Interaction" },
];

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const ScoreCell = ({ value, compare }) => {
  const improved = compare != null && value > compare;
  const declined = compare != null && value < compare;
  return (
    <span style={{ fontWeight: 600, color: improved ? "#16a34a" : declined ? "#dc2626" : "#374151" }}>
      {value}{improved ? " ↑" : declined ? " ↓" : ""}
    </span>
  );
};

const AssessmentRow = ({ student, initial, final }) => {
  const delta    = initial && final ? final.totalScore - initial.totalScore : null;
  const improved = delta != null && delta > 0;

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{student.studentName}</div>
        <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{student.studentEmail}</div>
      </td>
      {/* INITIAL */}
      {CRITERIA.map((c) => (
        <td key={`i-${c.key}`} style={{ textAlign: "center", fontSize: "0.85rem" }}>
          {initial ? initial[c.key] : <span style={{ color: "#d1d5db" }}>—</span>}
        </td>
      ))}
      <td style={{ textAlign: "center", fontWeight: 700, fontSize: "0.88rem", color: "#2563eb" }}>
        {initial ? `${initial.totalScore}/25` : <span style={{ color: "#d1d5db" }}>—</span>}
      </td>
      {/* FINAL */}
      {CRITERIA.map((c) => (
        <td key={`f-${c.key}`} style={{ textAlign: "center", fontSize: "0.85rem" }}>
          {final
            ? <ScoreCell value={final[c.key]} compare={initial?.[c.key] ?? null} />
            : <span style={{ color: "#d1d5db" }}>—</span>}
        </td>
      ))}
      <td style={{ textAlign: "center", fontWeight: 700, fontSize: "0.88rem", color: "#16a34a" }}>
        {final ? `${final.totalScore}/25` : <span style={{ color: "#d1d5db" }}>—</span>}
      </td>
      {/* Delta */}
      <td style={{ textAlign: "center" }}>
        {delta != null ? (
          <span style={{
            fontWeight: 700, fontSize: "0.85rem",
            color: improved ? "#16a34a" : delta < 0 ? "#dc2626" : "#6b7280",
          }}>
            {improved ? `+${delta}` : delta}
          </span>
        ) : <span style={{ color: "#d1d5db" }}>—</span>}
      </td>
      <td style={{ fontSize: "0.78rem", color: "#6b7280", textAlign: "center" }}>
        {initial ? fmtDate(initial.assessedAt) : "—"}
      </td>
    </tr>
  );
};

const AdminBatchAssessmentsPage = () => {
  const { id: courseId, batchId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows]       = useState([]);
  const [batchName, setBatchName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assessRes, batchRes] = await Promise.all([
        fetchBatchAssessments(batchId),
        fetchBatchDetail(courseId, batchId),
      ]);

      const data     = Array.isArray(assessRes.data) ? assessRes.data : [];
      const students = batchRes.data?.enrolledStudents || [];

      // Build enrollmentId → { userName, userEmail } lookup from batch detail
      const studentMap = {};
      students.forEach((s) => {
        studentMap[s.enrollmentId] = { name: s.userName, email: s.userEmail };
      });

      // Group by enrollmentId → { student, initial, final }
      const map = {};
      data.forEach((a) => {
        if (!map[a.enrollmentId]) {
          const info = studentMap[a.enrollmentId] || {};
          map[a.enrollmentId] = {
            student: {
              studentName:  info.name  || `Enrollment #${a.enrollmentId}`,
              studentEmail: info.email || "",
              enrollmentId: a.enrollmentId,
            },
            initial: null,
            final:   null,
          };
          if (!batchName && a.batchName) setBatchName(a.batchName);
        }
        if (a.assessmentType === "INITIAL") map[a.enrollmentId].initial = a;
        if (a.assessmentType === "FINAL")   map[a.enrollmentId].final   = a;
      });

      setRows(Object.values(map));
    } catch {
      setError("Failed to load batch assessments.");
    } finally {
      setLoading(false);
    }
  }, [batchId, courseId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="ac-loading">Loading assessments…</div>;

  const assessed   = rows.filter((r) => r.initial || r.final).length;
  const bothDone   = rows.filter((r) => r.initial && r.final).length;

  return (
    <div>
      <div className="ac-header">
        <div>
          <h2>Batch Assessments</h2>
          <div style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "0.2rem" }}>
            {batchName || `Batch #${batchId}`}
          </div>
        </div>
        <button className="ac-btn ac-btn-ghost" onClick={() => navigate(`/admin/courses/${courseId}/batches/${batchId}`)}>
          ← Back to Batch
        </button>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.6rem 0.9rem", borderRadius: 6, marginBottom: "1rem", fontSize: "0.88rem" }}>
          {error}
        </div>
      )}

      {/* Summary chips */}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {[
          ["Total Students", rows.length, "#2563eb", "#dbeafe"],
          ["Assessed",       assessed,    "#7c3aed", "#ede9fe"],
          ["Both Done",      bothDone,    "#16a34a", "#dcfce7"],
        ].map(([label, val, color, bg]) => (
          <div key={label} style={{ background: bg, borderRadius: 8, padding: "0.5rem 1rem", minWidth: 110 }}>
            <div style={{ fontSize: "0.72rem", color, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
            <div style={{ fontSize: "1.4rem", fontWeight: 800, color }}>{val}</div>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="ac-empty">No assessments submitted for this batch yet.</div>
      ) : (
        <div className="ac-table-wrap" style={{ overflowX: "auto" }}>
          <table className="ac-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ verticalAlign: "middle" }}>Student</th>
                <th colSpan={6} style={{ textAlign: "center", background: "#dbeafe", color: "#1d4ed8" }}>Initial Assessment</th>
                <th colSpan={6} style={{ textAlign: "center", background: "#dcfce7", color: "#15803d" }}>Final Assessment</th>
                <th rowSpan={2} style={{ verticalAlign: "middle", textAlign: "center" }}>Δ</th>
                <th rowSpan={2} style={{ verticalAlign: "middle", textAlign: "center" }}>Assessed At</th>
              </tr>
              <tr>
                {CRITERIA.map((c) => <th key={`ih-${c.key}`} style={{ textAlign: "center", fontSize: "0.75rem" }}>{c.label}</th>)}
                <th style={{ textAlign: "center", fontSize: "0.75rem" }}>Total</th>
                {CRITERIA.map((c) => <th key={`fh-${c.key}`} style={{ textAlign: "center", fontSize: "0.75rem" }}>{c.label}</th>)}
                <th style={{ textAlign: "center", fontSize: "0.75rem" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <AssessmentRow
                  key={r.student.enrollmentId}
                  student={r.student}
                  initial={r.initial}
                  final={r.final}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBatchAssessmentsPage;
